"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Script from "next/script";
import { useRouter } from "next/navigation";
import { FormStatus, SubmitButton } from "@/components/ui/Form";
import { useT } from "@/lib/i18n/context";
import { oauthButtonsTranslations } from "./OAuthButtons.i18n";
import styles from "./OAuthButtons.module.css";

/* ─── Window globals (FB SDK + Google Identity Services) ───────────────── */

declare global {
  interface Window {
    FB?: {
      init: (config: { appId: string; version: string; cookie?: boolean; xfbml?: boolean }) => void;
      login: (
        cb: (response: FbLoginResponse) => void,
        opts?: { scope?: string },
      ) => void;
      getLoginStatus?: (cb: (response: FbLoginResponse) => void) => void;
    };
    /** Idempotent flag — żeby `FB.init` nie był wywoływany 2x przy re-mount
     *  komponentu (powodowałoby warning "You are overriding current access token"). */
    __fbInited?: boolean;
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number | string;
              locale?: string;
            },
          ) => void;
          cancel: () => void;
        };
      };
    };
  }
}

type FbLoginResponse = {
  status: "connected" | "not_authorized" | "unknown";
  authResponse?: {
    accessToken: string;
    userID: string;
    expiresIn: number;
  };
};

/* ─── Component ─────────────────────────────────────────────────────────── */

export type OAuthProviders = {
  facebook: { enabled: boolean; app_id: string | null };
  google: { enabled: boolean; client_id: string | null };
};

/** Minimalny shape grupy usera potrzebny dla rejestracji po OAuth.
 *  Współdzielony przez konsumentów (CUserAccounts, WUserAccount itp.). */
export type OAuthUserGroup = {
  id: number;
  name: string;
};

export type OAuthButtonsProps = {
  providers: OAuthProviders;
  /** Grupa użytkowników do rejestracji nowych kont (gdy user nie istnieje
   *  w bazie po sukces FB/Google login -> quickRegister z tą grupą).
   *  Brany z `content.user_groups[0]` w konsumenta. */
  userGroups: OAuthUserGroup[];
  /** Język dla emaila rejestracyjnego (przekazywany do backendu jako `s_lang`). */
  sLang?: string;
  /** Wewnętrzna ścieżka powrotu po zalogowaniu (`?continue=` z gate'u
   *  after_login). Gdy podana, po sukces OAuth login nawigujemy tam zamiast
   *  `router.refresh()`. Caller MUSI przekazać już zsanityzowaną wartość
   *  (tylko `/...`, nie `//host`) — komponent jej nie waliduje ponownie.
   *  NULL/undefined -> domyślne `router.refresh()` (login na bieżącej stronie). */
  continueUrl?: string | null;
};

/**
 * OAuthButtons — przyciski "Zaloguj z Facebook" / "Zaloguj z Google".
 *
 * Shared komponent — używany przez CUserAccounts (katalog) i WUserAccount (okno).
 * Trzyma własne tłumaczenia (`oauthButtonsTranslations`) i typy.
 *
 * Komponent ładuje SDK Facebook i Google asynchronicznie (`next/script`),
 * inicjalizuje je z kluczami z `providers.{facebook,google}` (gating per-katalog
 * + per-domena na backendzie), i wysyła OAuth token (FB `accessToken` / Google
 * JWT `credential`) do proxy `/api/iiapi/facebook-login` / `/api/iiapi/google-login`.
 *
 * **Gating:**
 *   - Renderuje TYLKO providers gdzie `enabled === true`
 *   - Gdy oba `enabled === false` -> komponent zwraca null (zero footprint)
 *   - Gdy brak `userGroups` -> komponent zwraca null (rejestracja byłaby bez grupy)
 *
 * **SDK loading:**
 *   - FB SDK: ładowane TYLKO gdy `facebook.enabled === true`
 *   - Google SDK: ładowane TYLKO gdy `google.enabled === true`
 *   - `Script` `strategy="afterInteractive"` — load po hydration, nie blokuje
 *
 * **Po sukces login:** `router.refresh()` — chrome (HDefault, menu) re-fetchuje
 * SSR session i renderuje "Witaj, {name}" zamiast formularza loginowego.
 */
export function OAuthButtons({
  providers,
  userGroups,
  sLang,
  continueUrl = null,
}: OAuthButtonsProps) {
  const t = useT(oauthButtonsTranslations);
  const router = useRouter();

  /* Powrót po zalogowaniu: jeśli caller przekazał `continueUrl` (gate after_login),
     nawigujemy tam; inaczej tylko odświeżamy server components (login na bieżącej
     stronie). `router.push` na server route -> SSR widzi już cookie sesji. */
  const finishLogin = useCallback(() => {
    if (continueUrl) {
      router.push(continueUrl);
    } else {
      router.refresh();
    }
  }, [continueUrl, router]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"facebook" | "google" | null>(null);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [googleSdkReady, setGoogleSdkReady] = useState(false);
  const [fbSdkReady, setFbSdkReady] = useState(false);

  const fbEnabled = providers.facebook.enabled && !!providers.facebook.app_id;
  const googleEnabled = providers.google.enabled && !!providers.google.client_id;
  const idUserGroup = userGroups[0]?.id ?? 0;

  /* SDK lokalizacja — UI Imperial używa 2-char (`pl`/`en`/`de`), FB SDK
     wymaga `pl_PL`/`en_US`/`de_DE` w URL'u, Google wymaga BCP-47 (`pl`/`en`/`de`
     przeważnie wystarczy). Fallback dla nieobsługiwanych locale -> en_US/en. */
  const fbLocale = mapFbLocale(sLang);
  const googleLocale = sLang ?? "en";

  /* ─── Async submission helpers ───
     Wynosimy z onClick / SDK callback'ów żeby ŻADNA funkcja przekazywana
     bezpośrednio do `<button onClick>` / `FB.login(cb)` / `google.id.initialize({callback})`
     NIE była `async` — Next.js 16 / React 19 wywala wtedy runtime error
     "Expression is of type asyncfunction, not function". Wrappery sync
     wywołują te funkcje przez `void` (fire-and-forget). */

  const submitFacebookToken = useCallback(
    async (accessToken: string) => {
      try {
        const res = await fetch("/api/iiapi/facebook-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            access_token: accessToken,
            id_user_group: idUserGroup,
            ...(sLang ? { s_lang: sLang } : {}),
          }),
        });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
        } | null;
        if (!res.ok || !body?.ok) {
          setError(body?.message ?? t.facebookFailed);
          setBusy(null);
          return;
        }
        finishLogin();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setBusy(null);
      }
    },
    [idUserGroup, sLang, finishLogin, t],
  );

  const submitGoogleCredential = useCallback(
    async (credential: string) => {
      try {
        const res = await fetch("/api/iiapi/google-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            credential,
            id_user_group: idUserGroup,
            ...(sLang ? { s_lang: sLang } : {}),
          }),
        });
        const body = (await res.json().catch(() => null)) as {
          ok?: boolean;
          message?: string;
        } | null;
        if (!res.ok || !body?.ok) {
          setError(body?.message ?? t.googleFailed);
          setBusy(null);
          return;
        }
        finishLogin();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setBusy(null);
      }
    },
    [idUserGroup, sLang, finishLogin, t],
  );

  /* ─── Google Identity Services init + render button ─── */
  useEffect(() => {
    if (!googleEnabled || !googleSdkReady || !googleButtonRef.current) return;
    if (!window.google?.accounts?.id) return;

    window.google.accounts.id.initialize({
      client_id: providers.google.client_id!,
      /* SYNC callback — async submission via `void submitGoogleCredential(...)`. */
      callback: (response) => {
        if (!response?.credential) return;
        setBusy("google");
        setError(null);
        void submitGoogleCredential(response.credential);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    /* Wyczyść poprzedni render gdy SDK re-init (np. zmiana języka). */
    googleButtonRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(googleButtonRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      shape: "rectangular",
      logo_alignment: "center",
      width: 280,
      locale: googleLocale,
    });
  }, [googleEnabled, googleSdkReady, providers.google.client_id, submitGoogleCredential, googleLocale]);

  /* ─── FB SDK init helper (idempotent) ───
     Wywoływany przez `<Script onReady>` poniżej. Pojedynczy `FB.init`
     per page lifecycle — sprawdzamy `__fbInited` flag żeby uniknąć
     warning "You are overriding current access token" przy re-mount
     komponentu. */
  const initFbSdk = useCallback(() => {
    if (!window.FB) return;
    if (!window.__fbInited) {
      window.FB.init({
        appId: providers.facebook.app_id!,
        version: "v18.0",
        cookie: false,
        xfbml: false,
      });
      window.__fbInited = true;
    }
    setFbSdkReady(true);
  }, [providers.facebook.app_id]);

  /* ─── FB login handler (SYNC — async submission via `void`) ─── */
  const handleFbLogin = useCallback(() => {
    if (!window.FB || !fbSdkReady || busy) return;
    setBusy("facebook");
    setError(null);
    window.FB.login(
      /* SYNC callback — Next.js 16 nie pozwala na async w SDK callback'ach.
         FB.login zwraca status, akcję delegujemy do `submitFacebookToken`. */
      (response) => {
        if (response.status !== "connected" || !response.authResponse?.accessToken) {
          setError(t.facebookCancelled);
          setBusy(null);
          return;
        }
        void submitFacebookToken(response.authResponse.accessToken);
      },
      { scope: "email" },
    );
  }, [fbSdkReady, busy, t, submitFacebookToken]);

  if (!fbEnabled && !googleEnabled) return null;
  if (idUserGroup <= 0) return null;

  return (
    <div className={styles.root}>
      <div className={styles.divider} role="separator" aria-orientation="horizontal">
        <span className={styles.dividerLabel}>{t.dividerOr}</span>
      </div>

      <div className={styles.buttons}>
        {fbEnabled && (
          <>
            <Script
              src={`https://connect.facebook.net/${fbLocale}/sdk.js`}
              strategy="afterInteractive"
              /* `onReady` zamiast `onLoad` — wywoływane też przy re-mount
                 gdy SDK już załadowany (onLoad odpala się tylko raz na load).
                 `initFbSdk` jest idempotent (sprawdza `__fbInited` flag). */
              onReady={initFbSdk}
            />
            <SubmitButton
              type="button"
              variant="primary"
              onClick={handleFbLogin}
              disabled={!fbSdkReady || busy !== null}
              loading={busy === "facebook"}
              loadingLabel={t.signingIn}
              className={styles.fbButton}
            >
              <svg
                className={styles.fbIcon}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M22 12.06C22 6.51 17.52 2 12 2S2 6.51 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34V22c4.78-.75 8.44-4.92 8.44-9.94Z"
                />
              </svg>
              <span>{t.facebookButton}</span>
            </SubmitButton>
          </>
        )}

        {googleEnabled && (
          <>
            <Script
              src="https://accounts.google.com/gsi/client"
              strategy="afterInteractive"
              /* `onReady` zamiast `onLoad` — odpala się TEŻ przy re-mount
                 komponentu gdy SDK już załadowane (np. user zamknął i otworzył
                 ponownie modal WUserAccount). `onLoad` odpala się raz na load
                 skryptu — drugie otwarcie modal'a zostawiałoby
                 `googleSdkReady=false` i Google button nie renderował się. */
              onReady={() => setGoogleSdkReady(true)}
            />
            <div
              ref={googleButtonRef}
              className={styles.googleButton}
              aria-busy={busy === "google"}
            />
          </>
        )}
      </div>

      {error && (
        <div className={styles.errorWrap}>
          <FormStatus variant="error">{error}</FormStatus>
        </div>
      )}
    </div>
  );
}

/* ─── Locale mapper ─────────────────────────────────────────────────────
   FB SDK serwuje per-locale build z URL'a (`/pl_PL/sdk.js`, `/en_US/sdk.js`).
   Lista wszystkich kodów: https://lookaside.facebook.com/developers/resources/?id=FacebookLocales.xml
   Imperial UI używa 2-char ISO (pl/en/de) — mapujemy na FB BCP-47-like format.
   Nieobsługiwany locale -> fallback `en_US` (FB default). */
function mapFbLocale(uiLang: string | undefined): string {
  switch (uiLang) {
    case "pl":
      return "pl_PL";
    case "en":
      return "en_US";
    case "de":
      return "de_DE";
    default:
      return "en_US";
  }
}
